/**
 * SSOT-9 — per-report required intent fields.
 *
 * After SSOT, the eight previously-duplicated fields live exclusively on
 * `SiteEntity.projectIntent`. Each report type benefits when specific
 * intent fields are populated. The wizard + rerun render a non-blocking
 * banner when the site's intent lacks a required field for the active
 * report type — the user can still proceed (the LLM falls through to a
 * neutral framing) but the banner nudges them to the canonical editor.
 *
 * The map is intentionally curated, not exhaustive. The banner is meant
 * to surface the fields that materially change the report's framing for
 * a given type — listing every intent field would dilute the signal.
 *
 * Pure module — no React, no I/O. Safe to import from anywhere.
 *
 * Pack: docs/prompt-packs/Site_SSOT_Prompt.md
 */
import type { ProjectIntent } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import { isIntentFieldExplicitlyUnknown } from './intent/explicitUnknown';

/**
 * Identifier of a canonical `ProjectIntent` field. We use the
 * SiteEntity-side names (not the resolver's `InputFieldId` keys) because
 * this map is consumed by UI that opens the Project Intent editor — the
 * field names match the `data-field-id` attributes on `ProjectIntentForm`.
 */
export type RequiredIntentField =
  | 'workloadProfile'
  | 'powerRequirementMW'
  | 'powerRequirementMW-exact'
  | 'coolingStrategy'
  | 'bopHeatRejection'
  | 'nuclearInterest'
  | 'priority'
  | 'inServiceDate'
  | 'timeline';

/**
 * Per-report-type list of intent fields whose absence triggers a
 * missing-intent banner in the wizard / rerun. Curated for product
 * impact — see module docstring.
 */
export const REQUIRED_INTENT_FIELDS_BY_REPORT_TYPE: Record<ReportTypeId, ReadonlyArray<RequiredIntentField>> = {
  // Stage 1 can screen location and disclose unknown cooling design. Keep
  // design-specific guidance with the reports that consume that design.
  // Priority is not a screening input (the screening form does not ask for
  // it): screening compares every option, and priority shapes Stage 2 choices.
  feasibility:        ['workloadProfile', 'powerRequirementMW'],
  environmental:      ['workloadProfile', 'powerRequirementMW'],
  'test-fit':         ['powerRequirementMW'],
  'power-availability': ['powerRequirementMW', 'inServiceDate'],
  'water-resource':   ['bopHeatRejection', 'powerRequirementMW'],
  interconnection:    ['workloadProfile', 'powerRequirementMW'],
  'supply-chain-risk': ['powerRequirementMW', 'inServiceDate'],
  // PSR-1 (row 0.99290) — Public Sentiment Report. Scaffold has no
  // mandatory intent fields; PSR-2 may add `workloadProfile` once
  // per-BTM sentiment is hardened.
  'public-sentiment': [],
  'custom-brief': ['workloadProfile', 'powerRequirementMW', 'timeline'],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The project-finance
  // engine sizes off power + in-service date (capex / energy / discount basis).
  financial: ['powerRequirementMW', 'inServiceDate'],
  'executive-summary': ['workloadProfile', 'powerRequirementMW'],
};

/**
 * Human-readable label for each required intent field. Used by the
 * missing-intent banner.
 */
export const REQUIRED_INTENT_FIELD_LABEL: Record<RequiredIntentField, string> = {
  workloadProfile:            'Workload profile',
  powerRequirementMW:         'Power requirement (MW)', // SSOT-SENTINEL-OK — ProjectIntent field name (canonical home)
  'powerRequirementMW-exact': 'Power requirement (exact MW)',
  coolingStrategy:            'Cooling strategy',
  bopHeatRejection:           'Heat-rejection sink', // FA-2 (v1.2.0) — matches data-field-id on ProjectIntentForm
  nuclearInterest:            'Nuclear interest',
  priority:                   'Priority',
  inServiceDate:              'In-service date',
  timeline:                   'Target timing (date or range)',
};

/**
 * The `fieldSource` keys the intent editors stamp when the user records each
 * required field as unknown (IntentStage / ProjectIntentForm): power is
 * stamped on the bucket key and the in-service date on `timeline`.
 */
const EXPLICIT_UNKNOWN_KEYS: Record<RequiredIntentField, readonly string[]> = {
  workloadProfile:            ['workloadProfile'],
  powerRequirementMW:         ['powerRequirement'], // SSOT-SENTINEL-OK — RequiredIntentField id (ProjectIntent field name)
  'powerRequirementMW-exact': ['powerRequirement'],
  coolingStrategy:            ['coolingStrategy'],
  bopHeatRejection:           ['bopHeatRejection'],
  nuclearInterest:            ['nuclearInterest'],
  priority:                   ['priority'],
  inServiceDate:              ['timeline'],
  timeline:                   ['timeline'],
};

/**
 * Returns true when the intent's value for `field` is missing, undefined,
 * or the form's `undecided` sentinel. Mixed is a valid workload profile.
 * A field the user explicitly recorded as unknown is ANSWERED, not missing:
 * the value stays unknown, but the user is not asked for it again. Pure.
 */
function fieldIsMissing(intent: ProjectIntent | undefined | null, field: RequiredIntentField): boolean {
  if (!intent) return true;
  if (EXPLICIT_UNKNOWN_KEYS[field].some(key => isIntentFieldExplicitlyUnknown(intent, key))) {
    return false;
  }
  switch (field) {
    case 'workloadProfile': {
      const v = intent.workloadProfile;
      return !v;
    }
    case 'powerRequirementMW': {
      // NWX-1: accept either the exact MW OR a non-undecided bucket —
      // applies to ALL 6 active report types. The wizard chrome reads
      // `powerRequirement` (bucket) and shows "50–200 MW"; the validator
      // must agree, otherwise users see "missing: Power requirement (MW)"
      // while the same row of the form displays the bucket. Report types
      // that genuinely need exact precision (e.g. deep grid
      // interconnection, financial models) opt in via the dedicated
      // `'powerRequirementMW-exact'` field id below.
      const exact = intent.powerRequirementMW;
      const bucket = intent.powerRequirement;
      const hasExact = typeof exact === 'number' && Number.isFinite(exact) && exact > 0;
      const hasBucket = typeof bucket === 'string' && bucket !== 'undecided' && bucket.trim() !== '';
      return !hasExact && !hasBucket;
    }
    case 'powerRequirementMW-exact': {
      // NWX-1b — opt-in strict variant for report types that genuinely
      // need the exact MW value (none today; future deep-grid /
      // financial-modelling report types would add this field id to
      // their REQUIRED_INTENT_FIELDS_BY_REPORT_TYPE entry).
      const v = intent.powerRequirementMW;
      return typeof v !== 'number' || !Number.isFinite(v) || v <= 0;
    }
    case 'coolingStrategy': {
      const v = intent.coolingStrategy;
      return !v || v === 'undecided';
    }
    case 'bopHeatRejection': {
      // Allow 'unknown' as a valid user-defined option: if the user defines
      // bopHeatRejection as unknown, it is not missing and should not be required.
      // Only treat it as missing when completely absent / unset.
      const v = intent.bopHeatRejection;
      if (v === 'unknown' || isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection')) {
        return false;
      }
      return !v;
    }
    case 'nuclearInterest': {
      const v = intent.nuclearInterest;
      return !v || v === 'undecided';
    }
    case 'priority': {
      const v = intent.priority;
      return !v || v === 'undecided';
    }
    case 'timeline':
      return !intent.inServiceDate?.trim() && (!intent.timeline || intent.timeline === 'undecided');
    case 'inServiceDate': {
      const v = intent.inServiceDate;
      return typeof v !== 'string' || v.trim().length === 0;
    }
    default:
      return true;
  }
}

/**
 * Compute the list of required intent fields that are missing for a
 * given report type. Empty when the intent is fully populated.
 */
export function missingIntentFields(
  reportType: ReportTypeId,
  intent: ProjectIntent | undefined | null,
): RequiredIntentField[] {
  const required = REQUIRED_INTENT_FIELDS_BY_REPORT_TYPE[reportType] ?? [];
  return required.filter(f => fieldIsMissing(intent, f));
}

/**
 * Format the missing-fields list as a human-readable string for the
 * banner copy. Returns `''` when nothing is missing.
 */
export function missingIntentFieldsLabel(missing: ReadonlyArray<RequiredIntentField>): string {
  return missing.map(f => REQUIRED_INTENT_FIELD_LABEL[f]).join(', ');
}
